import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Charity, CharityStore, ENVIRONMENT, Environment, Pricing, Screening, Sponsorship, SponsorshipStatement } from '@domains';
import { Deserialize, Serialize } from 'cerialize';
import { catchError, map, Observable, switchMap } from 'rxjs';

import { BaseApiService } from '../base-api.service';
import { Invoice } from './../../../../domains/src/invoice';
import { MarketsService } from './markets.service';

@Injectable({
  providedIn: 'root',
})
export class CharityService extends BaseApiService<Charity> {
  constructor(
    @Inject(ENVIRONMENT) override config: Environment,
    override http: HttpClient,
    private marketsService: MarketsService,
  ) {
    super(config, http, 'charities', Charity, 'Charity', [], ['market']);
  }

  override deserialize(data: any): Charity {
    const res = new Charity({
      ...Deserialize(data, Charity),
      ...(data.stores
        ? {
            stores: data.stores.map((s: any) => new CharityStore(Deserialize(s, CharityStore))),
          }
        : {}),
      ...(data.screening
        ? {
            screening: new Screening(Deserialize(data.screening, Screening)),
          }
        : {}),
      ...(data.sponsorship
        ? {
            sponsorship: new Sponsorship(Deserialize(data.sponsorship, Sponsorship)),
          }
        : {}),
    });
    if (res.logo && !res.logo.startsWith('http')) {
      res.logo = this.config.urls.baseUrl + res.logo;
    }
    if (res.csm_photo_url && !res.csm_photo_url.startsWith('http')) {
      res.csm_photo_url = this.config.urls.baseUrl + res.csm_photo_url;
    }
    res.setInitialValue();
    return res;
  }

  override serialize(item: Charity) {
    return {
      ...Serialize(
        {
          ...item,
          ...(item.market
            ? {
                market: { ...this.marketsService.serialize(item.market) },
              }
            : {}),
        },
        Charity,
      ),
    };
  }

  createPaymentSetup(
    sponsorshipId: string,
    invoicing?: boolean,
  ): Observable<{
    client_secret: string;
  }> {
    return this.http.post<{
      client_secret: string;
    }>(`${this.config.urls.baseUrl}/sponsorships/${sponsorshipId}/payment/setup`, { invoicing: !!invoicing });
  }

  createPaymentInvoice(sponsorshipId: string, amount: number, auto_pay: boolean): Observable<any> {
    return this.http.post<{
      client_secret: string;
    }>(`${this.config.urls.baseUrl}/sponsorships/${sponsorshipId}/payment/invoice`, {
      auto_pay: auto_pay || null,
      amount: Pricing.formatPrice(amount * 100),
    });
  }

  checkPaymentOvercharge(sponsorshipId: string): Observable<{ amount: number }> {
    return this.http
      .get<{
        usd: number;
      }>(`${this.config.urls.baseUrl}/sponsorships/${sponsorshipId}/stripe_balance`)
      .pipe(map((res) => ({ amount: Pricing.formatPrice((res?.usd || 0) / 100) })));
  }

  getSponsorship(charityId: string, expand?: string): Observable<Sponsorship | null> {
    return this.http
      .get<any[]>(`${this.config.urls.baseUrl}/sponsorships`, {
        params: {
          charity_id: charityId,
          ...(expand ? { expand } : {}),
        },
      })
      .pipe(map((res) => (res.length > 0 ? new Sponsorship(Deserialize(res[0], Sponsorship)) : null)));
  }

  createSponsorship(charityId: string, sponsorship: Sponsorship): Observable<Sponsorship | null> {
    return this.http.post<any>(`${this.config.urls.baseUrl}/sponsorships`, Serialize(sponsorship, Sponsorship)).pipe(
      switchMap(() => this.getSponsorship(charityId)),
      catchError(this.handleError),
    );
  }

  updateSponsorship(charityId: string, sponsorship: Sponsorship): Observable<Sponsorship | null> {
    return this.http.put<any>(`${this.config.urls.baseUrl}/sponsorships/${sponsorship.id}`, Serialize(sponsorship, Sponsorship)).pipe(
      switchMap(() => this.getSponsorship(charityId)),
      catchError(this.handleError),
    );
  }

  updateCourierSettings(charityId: string, enable: boolean): Observable<Charity> {
    return this.http.post<any>(`${this.config.urls.baseUrl}/charities/${charityId}/courier_setup`, { enable }).pipe(
      switchMap(() => this.find(charityId)),
      catchError(this.handleError),
    );
  }

  getSponsorshipInvoices(sponsorshipId: string, year: number): Observable<Array<Invoice> | null> {
    return this.http
      .get<Array<Invoice>>(`${this.config.urls.baseUrl}/sponsorships/${sponsorshipId}/invoices`, {
        params: {
          year,
        },
      })
      .pipe(map((res) => (res.length > 0 ? res.map((x) => new Invoice(Deserialize(x, Invoice))) : null)));
  }

  getSponsorshipStatements(params: any): Observable<Array<SponsorshipStatement>> {
    return this.http
      .get<Array<SponsorshipStatement>>(`${this.config.urls.baseUrl}/sponsorship_statements`, {
        params,
      })
      .pipe(
        map((results) => (results.length > 0 ? results.map((x) => new SponsorshipStatement(Deserialize(x, SponsorshipStatement))) : [])),
      );
  }

  getSponsorshipStatement(statement_id: string): Observable<SponsorshipStatement> {
    return this.http
      .get<SponsorshipStatement>(`${this.config.urls.baseUrl}/sponsorship_statements/${statement_id}`)
      .pipe(map((result) => new SponsorshipStatement(Deserialize(result, SponsorshipStatement))));
  }
}
